import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { Empty, Skeleton, message } from 'antd';
import { useQueryClient } from '@tanstack/react-query';
import { translator } from '@vantiva-sa/homesight_humaui/Store/configStore';
import { apiCallback } from '@vantiva-sa/homesight_humaui/Services/Common';
import usePhotoResources from '../Hooks/usePhotoResources';
import { getResourcesQueryKey } from '../Hooks/useResourcesByType';
import GalleryPreviewModal from './GalleryPreviewModal';
import './Gallery.less';
import {
  DEFAULT_PHOTO_PLACEHOLDER,
  getPhotoKey,
  getPhotoMediaUrl,
  isHeicPhotoResource,
  convertHeicPhotoToDisplayUrl,
  GALLERY_BATCH_SIZE,
} from '@/utilities/helpers/galleryUtils';

/**
 * GalleryPhotos Component
 * Displays a grid of photos with metadata
 * Uses React Query for fetching photos - no thumbnail generation needed
 */
const GalleryPhotos = ({ hubRecord }) => {
  const trans = useSelector(translator);
  const shareResourcesApi = useSelector((state) => state.apis.shareResourcesApi);
  const queryClient = useQueryClient();
  const [activePhoto, setActivePhoto] = useState(null);
  const [isRemoving, setIsRemoving] = useState(false);
  const [visibleCount, setVisibleCount] = useState(GALLERY_BATCH_SIZE);
  const [convertedPhotoUrls, setConvertedPhotoUrls] = useState({});
  const [failedPhotoKeys, setFailedPhotoKeys] = useState({});
  const convertedPhotoUrlsRef = useRef({});
  const convertingPhotoKeysRef = useRef(new Set());
  const failedPhotoKeysRef = useRef(new Set());

  const revokeConvertedPhotoUrls = (urlMap) => {
    Object.values(urlMap || {}).forEach((url) => {
      if (typeof url === 'string' && url.startsWith('blob:')) {
        URL.revokeObjectURL(url);
      }
    });
  };

  const applyPhotoFallback = (imageElement) => {
    if (!imageElement || imageElement.dataset.fallbackApplied === 'true') {
      return;
    }

    imageElement.dataset.fallbackApplied = 'true';
    imageElement.classList.add('gallery-photo-image-fallback');
    imageElement.src = DEFAULT_PHOTO_PLACEHOLDER;
  };

  const cacheConvertedPhotoUrl = (photoKey, convertedUrl) => {
    if (!photoKey || !convertedUrl) {
      return;
    }

    setConvertedPhotoUrls((previousMap) => {
      const previousUrl = previousMap[photoKey];
      if (previousUrl && previousUrl !== convertedUrl && previousUrl.startsWith('blob:')) {
        URL.revokeObjectURL(previousUrl);
      }

      const nextMap = {
        ...previousMap,
        [photoKey]: convertedUrl,
      };

      convertedPhotoUrlsRef.current = nextMap;
      return nextMap;
    });
  };

  const convertPhotoIfNeeded = async (photo) => {
    const photoKey = getPhotoKey(photo);
    const sourceUrl = getPhotoMediaUrl(photo);

    if (!photoKey || !sourceUrl || !isHeicPhotoResource(photo)) {
      return '';
    }

    if (convertedPhotoUrlsRef.current[photoKey]) {
      return convertedPhotoUrlsRef.current[photoKey];
    }

    if (failedPhotoKeysRef.current.has(photoKey) || convertingPhotoKeysRef.current.has(photoKey)) {
      return '';
    }

    convertingPhotoKeysRef.current.add(photoKey);

    try {
      const convertedUrl = await convertHeicPhotoToDisplayUrl(sourceUrl);
      cacheConvertedPhotoUrl(photoKey, convertedUrl);
      failedPhotoKeysRef.current.delete(photoKey);
      setFailedPhotoKeys((previousMap) => {
        if (!previousMap[photoKey]) {
          return previousMap;
        }

        const nextMap = { ...previousMap };
        delete nextMap[photoKey];
        return nextMap;
      });
      return convertedUrl;
    } catch (conversionError) {
      failedPhotoKeysRef.current.add(photoKey);
      setFailedPhotoKeys((previousMap) => ({
        ...previousMap,
        [photoKey]: true,
      }));
      console.warn('Failed to convert HEIC photo for display:', conversionError);
      return '';
    } finally {
      convertingPhotoKeysRef.current.delete(photoKey);
    }
  };

  const handlePhotoLoadError = (event, photo) => {
    const imageElement = event.currentTarget;
    if (!photo) {
      applyPhotoFallback(imageElement);
      return;
    }

    const photoKey = getPhotoKey(photo);

    // Non-HEIC images should fall back immediately.
    if (!isHeicPhotoResource(photo) || !photoKey) {
      applyPhotoFallback(imageElement);
      return;
    }

    if (imageElement.dataset.conversionAttempted === 'true') {
      applyPhotoFallback(imageElement);
      return;
    }

    imageElement.dataset.conversionAttempted = 'true';

    convertPhotoIfNeeded(photo).then((convertedUrl) => {
      if (!convertedUrl) {
        applyPhotoFallback(imageElement);
        return;
      }

      imageElement.classList.remove('gallery-photo-image-fallback');
      imageElement.dataset.fallbackApplied = 'false';
      imageElement.src = convertedUrl;
    });
  };

  // Use hubRecord's useruuid (account being viewed), not logged-in user
  const accountUuid = hubRecord?.useruuid;

  // Fetch photos using React Query hook
  const {
    data: photos = [],
    isLoading,
    error,
  } = usePhotoResources({
    useruuid: accountUuid,
  });

  const visiblePhotos = photos.slice(0, visibleCount);
  const hasMorePhotos = visibleCount < photos.length;

  useEffect(() => {
    setVisibleCount(GALLERY_BATCH_SIZE);
  }, [accountUuid]);

  useEffect(() => {
    if (!hasMorePhotos) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) {
            return;
          }

          setVisibleCount((previousCount) =>
            Math.min(previousCount + GALLERY_BATCH_SIZE, photos.length)
          );
        });
      },
      {
        root: null,
        rootMargin: '300px 0px',
        threshold: 0,
      }
    );

    const sentinelElement = document.getElementById('gallery-photos-scroll-sentinel');
    if (sentinelElement) {
      observer.observe(sentinelElement);
    }

    return () => observer.disconnect();
  }, [hasMorePhotos, photos.length]);

  useEffect(() => {
    const photoKeys = new Set(photos.map((photo) => getPhotoKey(photo)).filter(Boolean));

    setConvertedPhotoUrls((previousMap) => {
      let hasRemovedEntries = false;
      const nextMap = { ...previousMap };

      Object.entries(previousMap).forEach(([photoKey, previewUrl]) => {
        if (photoKeys.has(photoKey)) {
          return;
        }

        hasRemovedEntries = true;
        if (typeof previewUrl === 'string' && previewUrl.startsWith('blob:')) {
          URL.revokeObjectURL(previewUrl);
        }

        delete nextMap[photoKey];
        failedPhotoKeysRef.current.delete(photoKey);
      });

      convertedPhotoUrlsRef.current = hasRemovedEntries ? nextMap : previousMap;
      return hasRemovedEntries ? nextMap : previousMap;
    });

    setFailedPhotoKeys((previousMap) => {
      const nextMap = { ...previousMap };
      let hasChanges = false;

      Object.keys(previousMap).forEach((photoKey) => {
        if (photoKeys.has(photoKey)) {
          return;
        }

        hasChanges = true;
        delete nextMap[photoKey];
      });

      return hasChanges ? nextMap : previousMap;
    });
  }, [photos]);

  useEffect(() => {
    let cancelled = false;

    const convertHeicPhotosSequentially = async () => {
      const heicPhotos = visiblePhotos.filter((photo) => isHeicPhotoResource(photo));

      for (const photo of heicPhotos) {
        if (cancelled) {
          return;
        }

        const photoKey = getPhotoKey(photo);
        if (!photoKey) {
          continue;
        }

        if (convertedPhotoUrlsRef.current[photoKey] || failedPhotoKeysRef.current.has(photoKey)) {
          continue;
        }

        // eslint-disable-next-line no-await-in-loop
        await convertPhotoIfNeeded(photo);
      }
    };

    convertHeicPhotosSequentially();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visiblePhotos]);

  useEffect(() => {
    return () => {
      revokeConvertedPhotoUrls(convertedPhotoUrlsRef.current);
      convertedPhotoUrlsRef.current = {};
    };
  }, []);

  const handleOpenPhoto = async (photo) => {
    const photoKey = getPhotoKey(photo);
    const sourceUrl = getPhotoMediaUrl(photo);
    let resolvedPhotoUrl = convertedPhotoUrlsRef.current[photoKey] || sourceUrl;

    if (!resolvedPhotoUrl) {
      return;
    }

    if (!convertedPhotoUrlsRef.current[photoKey] && isHeicPhotoResource(photo)) {
      const convertedUrl = await convertPhotoIfNeeded(photo);
      if (convertedUrl) {
        resolvedPhotoUrl = convertedUrl;
      }
    }

    setActivePhoto({ ...photo, resolvedPhotoUrl });
  };

  const unsharePhotoResource = async (resourceuuid) => {
    return new Promise((resolve, reject) => {
      shareResourcesApi.shareResources(
        accountUuid,
        'unshare',
        'photos',
        { resourceuuid: [resourceuuid] },
        apiCallback({
          translator: trans,
          successCallback: resolve,
          failCallback: reject,
          errorCallback: reject,
        })
      );
    });
  };

  const handleUnsharePhoto = async (photo) => {
    if (!photo?.resourceuuid || !shareResourcesApi || !accountUuid || isRemoving) {
      return;
    }

    setIsRemoving(true);

    try {
      await unsharePhotoResource(photo.resourceuuid);
      await queryClient.invalidateQueries({
        queryKey: getResourcesQueryKey({
          resourceType: 'photos',
          useruuid: accountUuid,
          shared: false,
        }),
      });
      setActivePhoto(null);
      message.success(trans('GALLERY_PREVIEW_UNSHARE_SUCCESS'));
    } catch (error) {
      console.error('Failed to unshare photo resource:', error);
      message.error(trans('GALLERY_PREVIEW_UNSHARE_ERROR'));
    } finally {
      setIsRemoving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="gallery-photos-container">
        <div className="gallery-photos-grid">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="gallery-photo-item">
              <div className="gallery-skeleton-image-slot gallery-skeleton-image-slot--square">
                <Skeleton.Image active style={{ width: '100%', height: '100%' }} />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error || !photos || photos.length === 0) {
    return (
      <div className="gallery-photos-container">
        <Empty description={trans('NODATAFOUND')} />
      </div>
    );
  }

  return (
    <div className="gallery-photos-container">
      <div className="gallery-photos-grid">
        {visiblePhotos.map((photo) => {
          const photoKey = getPhotoKey(photo);
          const isHeicPhoto = isHeicPhotoResource(photo);
          const isConvertedHeic = Boolean(convertedPhotoUrls[photoKey]);
          const hasFailedHeic = Boolean(failedPhotoKeys[photoKey]);
          const mediaUrl = convertedPhotoUrls[photoKey] || getPhotoMediaUrl(photo);

          return (
            <div
              key={photo.resourceuuid}
              className="gallery-photo-item"
              role="button"
              tabIndex={0}
              onClick={() => {
                handleOpenPhoto(photo);
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  handleOpenPhoto(photo);
                }
              }}
            >
              <div className="gallery-photo-image-wrapper">
                {isHeicPhoto && !isConvertedHeic && !hasFailedHeic ? (
                  <div className="gallery-skeleton-image-slot gallery-skeleton-image-slot--square">
                    <Skeleton.Image active style={{ width: '100%', height: '100%' }} />
                  </div>
                ) : (
                  <img
                    src={hasFailedHeic ? DEFAULT_PHOTO_PLACEHOLDER : mediaUrl}
                    alt={photo.title}
                    className="gallery-photo-image"
                    onError={(event) => handlePhotoLoadError(event, photo)}
                  />
                )}
              </div>
            </div>
          );
        })}
      </div>
      {hasMorePhotos && <div id="gallery-photos-scroll-sentinel" style={{ height: 1 }} />}

      <GalleryPreviewModal
        open={!!activePhoto}
        onClose={() => {
          if (!isRemoving) {
            setActivePhoto(null);
          }
        }}
        resource={activePhoto}
        resourceType="photos"
        onUnshare={handleUnsharePhoto}
        isRemoving={isRemoving}
        unshareLabel={trans('GALLERY_PREVIEW_REMOVE_FROM_ACCOUNT_ACTION')}
        photoFallbackSrc={DEFAULT_PHOTO_PLACEHOLDER}
      />
    </div>
  );
};

GalleryPhotos.propTypes = {
  hubRecord: PropTypes.object,
};

export default GalleryPhotos;
